import { Controller } from '@hotwired/stimulus';
import consumer from '../../channels/consumer';
import {
  embedSignature,
  onSignliftEvent,
  finalizeSignature,
  SIGNLIFT_EVENTS,
} from '../../lib/signlift';

const DOCLIFT_TIMEOUT = 180000;
const ERROR_DELAY = 5000;
const FINALIZATION_RELOAD_DELAY = 4000;

export default class extends Controller {
  static targets = [
    'step2',
    'step3',
    'step4',
    'error',
    'iframeContainer',
    'contractLoadedButton',
    'stepTitle',
    'loader',
    'successImage',
    'docInProgress',
    'docReady',
  ];

  static values = {
    subscriptionId: String,
    titleInCaseOfDocReady: String,
  };

  connect() {
    this.signingUrl = null;
    this.finalizeUrl = null;
    this.stopListening = null;
    this.docliftTimer = setTimeout(() => this.onDocliftTimeout(), DOCLIFT_TIMEOUT);

    this.subscription = consumer.subscriptions.create(
      {
        channel: 'SignatureChannel',
        subscription_id: this.subscriptionIdValue,
      },
      {
        received: (data) => this.received(data),
        rejected: () => this.scheduleError(),
      }
    );
  }

  disconnect() {
    clearTimeout(this.docliftTimer);
    if (this.stopListening) this.stopListening();
    if (this.subscription) this.subscription.unsubscribe();
  }

  received(data) {
    try {
      if (data.status !== 'success') {
        if (data.status === 'awaiting_finalization') {
          clearTimeout(this.docliftTimer);
          this.scheduleFinalizationReload();
          return;
        }

        return this.scheduleError();
      }

      clearTimeout(this.docliftTimer);

      if (data.redirection_url) {
        window.location = data.redirection_url;
        return;
      }

      if (data.signing_url) {
        this.prepareSignature(data.signing_url, data.finalize_url);
      }
    } catch {
      this.scheduleError();
    }
  }

  prepareSignature(signingUrl, finalizeUrl) {
    this.signingUrl = signingUrl;
    this.finalizeUrl = finalizeUrl;

    const iframe = embedSignature(this.iframeContainerTarget, signingUrl);
    if (iframe) {
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      iframe.style.border = '0';
    }
    if (this.stopListening) this.stopListening();
    this.stopListening = onSignliftEvent(
      (event) => this.onSignatureEvent(event),
      signingUrl,
      iframe
    );

    this.markDocumentReady();
  }

  markDocumentReady() {
    if (this.hasContractLoadedButtonTarget) {
      this.contractLoadedButtonTarget.disabled = false;
    }
    if (this.hasStepTitleTarget && this.hasTitleInCaseOfDocReadyValue) {
      this.stepTitleTarget.textContent = this.titleInCaseOfDocReadyValue;
    }
    if (this.hasLoaderTarget) this.loaderTarget.classList.add('d-none');
    if (this.hasSuccessImageTarget) this.successImageTarget.classList.remove('d-none');
    if (this.hasDocInProgressTarget) this.docInProgressTarget.classList.add('d-none');
    if (this.hasDocReadyTarget) this.docReadyTarget.classList.remove('d-none');
  }

  showSignatureFrame() {
    this.step2Target.classList.add('d-none');
    this.step3Target.classList.remove('d-none');
    this.updateBreadcrumb(3);
  }

  onSignatureEvent(event) {
    if (event.type === SIGNLIFT_EVENTS.SUCCESS) {
      finalizeSignature(this.finalizeUrl).then(() => window.location.reload());
    } else if (event.type === SIGNLIFT_EVENTS.ERROR) {
      this.scheduleError();
    }
  }

  onDocliftTimeout() {
    if (!this.signingUrl) this.showError();
  }

  scheduleError() {
    setTimeout(() => this.showError(), ERROR_DELAY);
  }

  scheduleFinalizationReload() {
    setTimeout(() => window.location.reload(), FINALIZATION_RELOAD_DELAY);
  }

  showError() {
    [this.step2Target, this.step3Target, this.step4Target].forEach((target) => {
      if (target) target.classList.add('d-none');
    });
    if (this.hasErrorTarget) this.errorTarget.classList.remove('d-none');
  }

  updateBreadcrumb(step) {
    const steps = document.querySelectorAll('.subscription-step');
    steps.forEach((element, index) => {
      element.classList.remove('current', 'past', 'next');
      const position = index + 1;
      if (position < step) element.classList.add('past');
      else if (position === step) element.classList.add('current');
      else element.classList.add('next');
    });

    const current = document.querySelector('#current_step');
    if (current) current.textContent = String(step);
  }
}
